import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle, ChevronRight, Loader2, Building2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import { RelatedBlogs } from './BlogIntegrated';

const IndustryPage = () => {
  const { industrySlug, subSlug } = useParams();
  const [industry, setIndustry] = useState(null);
  const [subItem, setSubItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchIndustry = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/cms/industry-menu/by-slug/${industrySlug}`
        );
        if (!response.ok) throw new Error('Industry not found');
        const data = await response.json();
        setIndustry(data);
        
        if (subSlug && data.sub_items) {
          const sub = data.sub_items.find(item => item.slug === subSlug);
          setSubItem(sub || null);
        } else {
          setSubItem(null);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchIndustry();
  }, [industrySlug, subSlug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-[#2a2a2a]" />
      </div>
    );
  }

  if (error || !industry) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <Building2 size={48} className="text-gray-300 mb-4" />
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Industry Not Found</h1>
        <p className="text-gray-600 mb-6">The industry you're looking for doesn't exist.</p>
        <Button asChild>
          <Link to="/industries">View All Industries</Link>
        </Button>
      </div>
    );
  }

  if (subItem) {
    return <SubItemPage industry={industry} subItem={subItem} />;
  }

  const heroTitle = industry.hero_title || industry.name;
  const heroSubtitle = industry.hero_subtitle || industry.description || '';
  const heroImage = industry.hero_image || 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=1200';
  const features = industry.features || [];
  const benefits = industry.benefits || [];
  const content = industry.content || '';
  const contentImage = industry.content_image || '';
  const caseStudies = industry.case_studies || [];
  const subItems = industry.sub_items || [];

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title={`${industry.name} Solutions | Navaris Digital`}
        description={heroSubtitle}
      />

      {/* Full-Width Hero Section */}
      <section className="relative min-h-[70vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src={heroImage} 
            alt={industry.name}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1a1a1a]/95 via-[#1a1a1a]/80 to-[#1a1a1a]/50" />
        </div>

        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
          <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
        </div>

        <div className="container mx-auto px-4 relative z-10 py-28">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-2 text-white/60 text-sm mb-4"
          >
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <ChevronRight size={14} />
            <Link to="/industries" className="hover:text-white transition-colors">Industries</Link>
            <ChevronRight size={14} />
            <span className="text-white">{industry.name}</span>
          </motion.div>

          <div className="max-w-3xl">
            <motion.span 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="inline-block px-4 py-1.5 bg-white/10 backdrop-blur-sm rounded-full text-white/80 text-sm mb-6"
            >
              Industry Solutions
            </motion.span>
            
            <motion.h1 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight"
            >
              {heroTitle}
            </motion.h1>
            
            <motion.p 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-xl text-white/80 mb-8 leading-relaxed"
            >
              {heroSubtitle}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="flex flex-wrap gap-4"
            >
              <Button asChild size="lg" className="bg-white text-[#1a1a1a] hover:bg-gray-100 text-base px-8">
                <Link to="/contact">
                  Start a Project <ArrowRight className="ml-2" size={18} />
                </Link>
              </Button>
              {caseStudies.length > 0 && (
                <Button asChild size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 text-base px-8">
                  <a href="#case-studies">View Case Studies</a>
                </Button>
              )}
            </motion.div>
          </div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="absolute bottom-8 right-8 hidden lg:flex items-center gap-8"
          >
            <div className="text-center">
              <div className="text-4xl font-bold text-white">{subItems.length}</div>
              <div className="text-white/60 text-sm">Solutions</div>
            </div>
            <div className="w-px h-12 bg-white/20" />
            <div className="text-center">
              <div className="text-4xl font-bold text-white">{caseStudies.length}</div>
              <div className="text-white/60 text-sm">Case Studies</div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-xs uppercase tracking-[0.2em] text-gray-400 font-medium">Overview</span>
              <h2 className="text-3xl font-bold text-[#2a2a2a] mt-2 mb-4">
                About {industry.name}
              </h2>
              <p className="text-gray-600 leading-relaxed whitespace-pre-line mb-6">
                {content || `We provide comprehensive technology solutions tailored specifically for the ${industry.name.toLowerCase()} sector.`}
              </p>
              
              {benefits.length > 0 && (
                <div>
                  <h3 className="font-semibold text-[#2a2a2a] mb-3">Key Benefits</h3>
                  <ul className="space-y-2">
                    {benefits.map((benefit, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <CheckCircle className="text-green-500 flex-shrink-0 mt-0.5" size={18} />
                        <span className="text-gray-700">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              {contentImage ? (
                <div className="relative rounded-2xl overflow-hidden shadow-xl">
                  <img 
                    src={contentImage} 
                    alt={`${industry.name} illustration`}
                    className="w-full h-auto"
                  />
                </div>
              ) : (
                <div className="bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl p-12 text-center aspect-video flex items-center justify-center">
                  <Building2 size={64} className="text-gray-300" />
                </div>
              )}
              
              {features.length > 0 && (
                <div className="absolute -bottom-6 -left-6 bg-[#2a2a2a] text-white rounded-xl p-4 shadow-xl max-w-xs hidden lg:block">
                  <h4 className="font-semibold mb-1 text-sm">Core Capabilities</h4>
                  <p className="text-white/70 text-xs">{features.slice(0, 2).join(' • ')}</p>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Compact Solutions Section */}
      {subItems.length > 0 && (
        <section className="py-10 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold text-[#2a2a2a]">Our Solutions</h2>
                <p className="text-gray-500 text-sm">{subItems.length} specialized services</p>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              {subItems.map((solution, index) => (
                <div
                  key={solution.id}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-lg border border-gray-200"
                >
                  <span className="w-5 h-5 bg-gray-100 rounded text-[10px] font-bold flex items-center justify-center text-gray-500">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <span className="font-medium text-sm text-[#2a2a2a]">{solution.name}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
      {/* Case Studies Section */}
      {caseStudies.length > 0 && (
        <section id="case-studies" className="py-12 bg-white">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold text-[#2a2a2a]">Case Studies</h2>
                <p className="text-gray-500 text-sm">Success stories in {industry.name.toLowerCase()}</p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-3">
              {caseStudies.map((cs) => (
                <div key={cs.id} className="bg-gray-50 rounded-lg overflow-hidden flex">
                  {cs.image && (
                    <div className="w-32 flex-shrink-0">
                      <img src={cs.image} alt={cs.title} className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div className="p-3 flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="px-1.5 py-0.5 bg-[#2a2a2a] text-white text-[10px] rounded font-medium">{cs.client}</span>
                    </div>
                    <h3 className="font-semibold text-[#2a2a2a] text-sm mb-1 line-clamp-1">{cs.title}</h3>
                    {cs.results && (
                      <p className="text-green-600 text-xs font-medium">{cs.results}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Related Blog Posts */}
      {industry && (
        <RelatedBlogs 
          industryIds={[industry.id]} 
          title={`Insights for ${industry.name}`}
          limit={3}
        />
      )}

      {/* CTA Section */}
      <section className="py-10 bg-[#2a2a2a]">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h2 className="text-xl font-bold text-white">Ready to Transform Your {industry.name.split(' ')[0]} Business?</h2>
              <p className="text-white/60 text-sm">Let's discuss your goals</p>
            </div>
            <Button asChild className="bg-white text-[#2a2a2a] hover:bg-gray-100 w-fit">
              <Link to="/contact">
                Get Started <ArrowRight className="ml-2" size={16} />
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

// Sub-item page
const SubItemPage = ({ industry, subItem }) => {
  const heroTitle = subItem.hero_title || subItem.name;
  const heroSubtitle = subItem.hero_subtitle || subItem.description || '';
  const heroImage = subItem.hero_image || industry.hero_image || 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=1200';
  const features = subItem.features || [];
  const benefits = subItem.benefits || [];
  const content = subItem.content || '';
  const contentImage = subItem.content_image || '';

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title={`${subItem.name} | ${industry.name} | Navaris Digital`}
        description={heroSubtitle}
      />

      {/* Full-Width Hero */}
      <section className="relative min-h-[60vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={heroImage} alt={subItem.name} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1a1a1a]/95 via-[#1a1a1a]/80 to-[#1a1a1a]/50" />
        </div>

        <div className="container mx-auto px-4 relative z-10 py-24">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-2 text-white/60 text-sm mb-4"
          >
            <Link to="/" className="hover:text-white">Home</Link>
            <ChevronRight size={14} />
            <Link to="/industries" className="hover:text-white">Industries</Link>
            <ChevronRight size={14} />
            <Link to={`/industries/${industry.slug}`} className="hover:text-white">{industry.name}</Link>
            <ChevronRight size={14} />
            <span className="text-white">{subItem.name}</span>
          </motion.div>

          <div className="max-w-3xl">
            <motion.h1 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-4xl md:text-5xl font-bold text-white mb-4"
            >
              {heroTitle}
            </motion.h1>
            
            <motion.p 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-xl text-white/80 mb-8"
            >
              {heroSubtitle}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <Button asChild size="lg" className="bg-white text-[#1a1a1a] hover:bg-gray-100">
                <Link to="/contact">Get Started <ArrowRight className="ml-2" size={18} /></Link>
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-14">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-3 gap-10">
            <div className="lg:col-span-2">
              <h2 className="text-2xl font-bold text-[#2a2a2a] mb-4">About {subItem.name}</h2>
              
              {contentImage && (
                <img src={contentImage} alt={subItem.name} className="w-full rounded-xl mb-6" />
              )}
              
              <p className="text-gray-600 leading-relaxed whitespace-pre-line mb-8">
                {content || `Comprehensive ${subItem.name.toLowerCase()} solutions tailored for ${industry.name.toLowerCase()} organizations.`}
              </p>

              {(features.length > 0 || benefits.length > 0) && (
                <div className="grid md:grid-cols-2 gap-6">
                  {features.length > 0 && (
                    <div className="bg-gray-50 rounded-xl p-5">
                      <h3 className="font-bold text-[#2a2a2a] mb-3">Key Features</h3>
                      <ul className="space-y-2">
                        {features.map((f, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                            <CheckCircle size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
                            <span>{f}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {benefits.length > 0 && (
                    <div className="bg-gray-50 rounded-xl p-5">
                      <h3 className="font-bold text-[#2a2a2a] mb-3">Benefits</h3>
                      <ul className="space-y-2">
                        {benefits.map((b, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                            <span className="w-5 h-5 bg-[#2a2a2a] rounded text-white text-xs flex items-center justify-center flex-shrink-0">{i+1}</span>
                            <span>{b}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div>
              <div className="sticky top-24 space-y-4">
                {industry.sub_items?.length > 1 && (
                  <div className="bg-gray-50 rounded-xl p-5">
                    <h3 className="font-bold text-[#2a2a2a] mb-3">Other Solutions</h3>
                    <div className="space-y-1">
                      {industry.sub_items.filter(s => s.slug !== subItem.slug).map((s) => (
                        <Link
                          key={s.id}
                          to={`/industries/${industry.slug}/${s.slug}`}
                          className="flex items-center justify-between py-2 px-3 text-sm text-gray-600 hover:text-[#2a2a2a] hover:bg-white rounded-lg transition-colors"
                        >
                          <span>{s.name}</span>
                          <ArrowRight size={14} className="opacity-0 group-hover:opacity-100" />
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                <div className="bg-[#2a2a2a] rounded-xl p-5 text-white">
                  <h3 className="font-bold mb-2">Need Help?</h3>
                  <p className="text-white/70 text-sm mb-4">Let's discuss how {subItem.name.toLowerCase()} can benefit your organization.</p>
                  <Button asChild className="w-full bg-white text-[#2a2a2a] hover:bg-gray-100">
                    <Link to="/contact">Contact Us</Link>
                  </Button>
                </div>

                <Link
                  to={`/industries/${industry.slug}`}
                  className="flex items-center gap-2 text-sm text-gray-600 hover:text-[#2a2a2a] p-2"
                >
                  <ChevronRight size={16} className="rotate-180" />
                  Back to {industry.name}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Blog Posts */}
      {industry && (
        <RelatedBlogs 
          industryIds={[industry.id]} 
          title={`Insights for ${subItem?.name || industry.name}`}
          limit={3}
        />
      )}
    </div>
  );
};

export default IndustryPage;
